import { DateTime } from '@ksyos/date-time';
import { getSelectedLanguage } from '@frontend/translate';

/**
 * Replacement for DisplayDatePassed
 *
 * Formats a date string into a relative date format (e.g. "Today", "Yesterday", "Last Monday", etc.)
 * If isTimeHidden is false, it will show relative time for today's dates (e.g. "2 hours ago")
 * For other dates it shows a calendar format
 * The output is localized based on the selected language
 *
 * @example
 * // With isTimeHidden = true
 * const now = DateTime.now();
 * formatRelativeDate("2024-01-01", now) // Returns "Today" if now is Jan 1st 2024
 * formatRelativeDate("2023-12-31", now) // Returns "Yesterday" if now is Jan 1st 2024
 * formatRelativeDate("2023-12-25", now) // Returns "Last Monday" if Dec 25th 2023 was a Monday
 * formatRelativeDate("2023-12-20", now) // Returns "12/20/2023" or "20-12-2023" based on locale
 *
 * // With isTimeHidden = false
 * formatRelativeDate("2024-01-01T10:00:00", now, false) // Returns "2 hours ago" if current time is 12:00
 * formatRelativeDate("2023-12-31", now, false) // Returns "12/31/2023" or "31-12-2023" based on locale
 */
export function formatRelativeDate(
    value: DateTime | string | null | undefined,
    compareWithDate: DateTime | Date | string,
    isTimeHidden = false
): string {
    if (!value) {
        return '';
    }

    const translate = getSelectedLanguage();
    const date = DateTime.from(value);
    const referenceDay = DateTime.from(compareWithDate).startOf('day');

    if (!isTimeHidden) {
        return date.isSame(referenceDay, 'd') ? date.formatFromNow() : date.formatFromNowAsCalendar();
    }

    const formattedDate = date.formatFromNowAsCalendar(null, {
        lastDay: `[${translate.common.date_yesterday}]`,
        lastWeek: `[${translate.common.date_last}] dddd`,
        nextDay: `[${translate.common.date_tomorrow}]`,
        nextWeek: 'l',
        sameDay: `[${translate.common.date_today}]`,
        sameElse: 'l',
    });

    return formattedDate.replace(/^[a-z]/g, (letter) => letter.toUpperCase());
}
